import _ from 'underscore';
import Backbone from "backbone";
import Chaplin from "chaplin";
import ProjectSharePopupView from "views/guest/ProjectSharePopupView";
import TrebbleClientAPIHelper from "models/helper/TrebbleClientAPI";
import LocalStorageHelper from "models/helper/LocalStorageHelper";
import PersistentModels from "services/PersistentModels";
import Utils from "models/helper/Utils";
import { shouldShowUpgradeCTA, getRecommendedUpgradeMode } from "models/helper/WorkspaceUpgradeCTA";
import ti18n from "i18n!nls/GuestFeaturei18n";
import RSVP from "rsvp";

// Backend throws 403 when a non-paid user tries to invite a reviewer. Older
// personal guest invites use "A Pro subscription is required to invite
// guests"; newer workspace materialization gates use "A paid plan is required
// to invite members or reviewers". ExpectedError doesn't carry a structured
// errorCode on the wire yet — TODO: have the backend tag this rejection with
// errorCode='guest/pro_required' so we can drop the message-substring check.
//
// Until then we require BOTH signals to match before routing into the
// upgrade flow:
//   - status 403 (the canonical Pro-required HTTP code), AND
//   - the message substring (so a different 403 like a permission
//     check doesn't accidentally trigger the upgrade dialog)
//
// Either signal alone would be too loose: status-only would catch every
// 403 (project access denied, missing membership, etc.); message-only
// silently breaks if the backend rewords the string. The intersection
// is conservative and explicit.
function _isProRequiredError(error) {
    if (!error) return false;
    var message = (error.message || error.errorMessage || '').toString();
    var normalizedMessage = message.toLowerCase();
    var status = error.statusCode || error.status || error.httpStatusCode;
    var statusOk = status === 403 || status === '403';
    var messageOk = message.indexOf('Pro subscription is required') !== -1
        || (normalizedMessage.indexOf('paid plan is required') !== -1
            && (normalizedMessage.indexOf('invite') !== -1
                || normalizedMessage.indexOf('reviewer') !== -1
                || normalizedMessage.indexOf('member') !== -1));
    return statusOk && messageOk;
}

const ProjectSharePopupController = Chaplin.Controller.extend({

    initialize: function(options) {
        _.extend(this, Backbone.Events);
        this._parentController = options.parentController;
        this._projectId = options.projectId;
        this._projectName = options.projectName;
        this._currentGuests = [];
        this._canInviteMoreGuests = true;
        this._guestLoadErrorMessage = null;
        this._hasReliableGuestCache = false;
        this._reviewerUsage = null; // { current: number, limit: number|"Unlimited"|null }
    },

    getModel: function() {
        return this.model;
    },

    getView: function() {
        return this.view;
    },

    onPopupClosed: function() {
        // Cleanup if needed
    },

    _isInWorkspaceContext: function() {
        return LocalStorageHelper.getInstance().isInWorkspaceContext();
    },

    _isUnlimitedValue: function(value) {
        if (value === null || value === undefined) {
            return false;
        }
        var strValue = String(value).toLowerCase().trim();
        return strValue === 'unlimited' || strValue === 'infinity' || strValue === 'inf' || strValue === '-1';
    },

    _computeCanInviteMoreGuests: function(guests) {
        if (this._isInWorkspaceContext()) {
            // In workspace context, check reviewer usage from the workspace plan
            var usage = this._reviewerUsage;
            if (!usage) {
                // Usage data not loaded yet — allow optimistically until data arrives
                return true;
            }
            if (this._isUnlimitedValue(usage.limit)) {
                return true;
            }
            var limit = typeof usage.limit === 'number' ? usage.limit : parseInt(usage.limit, 10);
            if (isNaN(limit)) {
                // Limit is unknown or not a number — allow optimistically
                return true;
            }
            // Prefer the local guests list when provided so that optimistic
            // invites/revokes are reflected immediately. Fall back to
            // _reviewerUsage.current only when the caller did not pass guests
            // (e.g., before the initial load completes).
            var current;
            if (Array.isArray(guests)) {
                current = 0;
                for (var k = 0; k < guests.length; k++) {
                    var guestStatus = guests[k].status;
                    if (guestStatus === 'active' || guestStatus === 'invited') {
                        current++;
                    }
                }
            } else {
                current = typeof usage.current === 'number' ? usage.current : parseInt(usage.current, 10);
                if (isNaN(current)) {
                    current = 0;
                }
            }
            return current < limit;
        }

        // Non-workspace context: enforce free-tier limit of 1 reviewer
        var guestList = Array.isArray(guests) ? guests : [];
        var activeOrInvitedCount = 0;
        for (var i = 0; i < guestList.length; i++) {
            var status = guestList[i].status;
            if (status === 'active' || status === 'invited') {
                activeOrInvitedCount++;
            }
        }

        return activeOrInvitedCount < 1;
    },

    _setGuestState: function(guests, canInviteMoreGuests, guestLoadErrorMessage, hasReliableGuestCache) {
        this._currentGuests = Array.isArray(guests) ? guests : [];
        this._canInviteMoreGuests = canInviteMoreGuests;
        this._guestLoadErrorMessage = guestLoadErrorMessage || null;
        if (hasReliableGuestCache !== undefined) {
            this._hasReliableGuestCache = hasReliableGuestCache;
        }

        if (this.view) {
            this.view.updateGuests(this._currentGuests, this._canInviteMoreGuests, this._guestLoadErrorMessage);
        }
    },

    _applyOptimisticInvite: function(email, accessLevel) {
        var trimmedEmail = email ? email.trim() : '';
        if (!trimmedEmail) {
            return;
        }

        var lowerEmail = trimmedEmail.toLowerCase();
        var nextGuests = this._currentGuests.slice();
        var didUpdateExistingGuest = false;

        for (var i = 0; i < nextGuests.length; i++) {
            var existingGuestEmail = (nextGuests[i].email || '').toLowerCase();
            if (existingGuestEmail === lowerEmail) {
                nextGuests[i] = Object.assign({}, nextGuests[i], {
                    email: trimmedEmail,
                    accessLevel: accessLevel,
                    status: 'invited'
                });
                didUpdateExistingGuest = true;
                break;
            }
        }

        if (!didUpdateExistingGuest) {
            nextGuests = nextGuests.concat([{
                guestId: '',
                email: trimmedEmail,
                accessLevel: accessLevel,
                status: 'invited'
            }]);
        }

        this._setGuestState(nextGuests, this._computeCanInviteMoreGuests(nextGuests), null, true);
    },

    _applyOptimisticRevoke: function(guestId) {
        var nextGuests = this._currentGuests.filter(function(guest) {
            var guestIdentity = guest.guestRecordId || guest.guestId || guest.email;
            return guestIdentity !== guestId;
        });

        this._setGuestState(nextGuests, this._computeCanInviteMoreGuests(nextGuests), null, true);
    },

    handleInvite: async function(email, accessLevel) {
        try {
            await TrebbleClientAPIHelper.getInstance().inviteGuestToProject({
                projectId: this._projectId,
                guestEmail: email,
                accessLevel: accessLevel
            });
            window.alertSuccessMessage(window.getI18n(ti18n, "INVITE_SUCCESS"));
            this._applyOptimisticInvite(email, accessLevel);
            // Reload guests after successful invite
            this._loadGuests();
        } catch (error) {
            console.error('Guest invite error:', error);
            // Upgrade-required failures (free user trying to invite) get a
            // dedicated upgrade-CTA dialog instead of the generic
            // "Could not send invite" alert. The generic alert was a
            // dead end — the user couldn't tell why the invite failed
            // OR what to do about it. The CTA dialog makes the limit
            // actionable: "Cancel" closes; "Upgrade" opens the unified
            // UpgradeToTeam popup so the user can pick Starter/Pro/Team
            // in one flow.
            //
            // BUT: only when the user can actually upgrade. If
            // shouldShowUpgradeCTA returns false (e.g. they already
            // own a paid workspace and the failure is a different
            // limit like per-seat), we'd be presenting a CTA whose
            // confirm button can't fix the problem. Fall back to the
            // generic alert in that case so the user at least sees
            // SOME error, rather than a dialog whose Upgrade button
            // silently no-ops.
            if (_isProRequiredError(error) && shouldShowUpgradeCTA()) {
                error.handledByUpgradePrompt = true;
                this._promptUpgradeFromInviteBlocked();
                throw error;
            }
            window.alertErrorMessage(window.getI18n(ti18n, "INVITE_ERROR"));
            throw error;
        }
    },

    _promptUpgradeFromInviteBlocked: function() {
        var title = window.getI18n(ti18n, "PRO_REQUIRED_TO_INVITE_TITLE")
            || "A Pro subscription is required to invite guests";
        var message = window.getI18n(ti18n, "PRO_REQUIRED_TO_INVITE_MESSAGE")
            || "Upgrade your plan to invite guests to your projects.";
        var cancelLabel = window.getI18n(ti18n, "CANCEL") || "Cancel";
        var upgradeLabel = window.getI18n(ti18n, "UPGRADE") || "Upgrade";

        // Use the standard confirmation dialog with Upgrade as the confirm
        // button. The legacy generic-error alert is replaced — the user
        // can either dismiss (Cancel) or jump directly to the upgrade
        // flow (Upgrade) without leaving the share context.
        // The caller (handleInvite) already gates this method on
        // shouldShowUpgradeCTA, so by the time we reach here the user
        // CAN upgrade. We don't re-check here on confirm because the
        // gate state can't realistically change between the user
        // seeing the dialog and clicking Upgrade in the same render
        // tick. Re-checking would only add a dead-end branch.
        Utils.getInstance().showUserConfirmationDialog(
            title,
            message,
            null,
            { buttonLabels: [cancelLabel, upgradeLabel] }
        ).then(function(confirmed) {
            if (!confirmed) return;
            var mode = getRecommendedUpgradeMode();
            PersistentModels.getInstance().getRouter().showUpgradeToTeamPopup(
                this._parentController || this,
                { mode: mode }
            );
        }.bind(this));
    },

    handleRevoke: async function(guestId) {
        try {
            await TrebbleClientAPIHelper.getInstance().revokeGuestAccess({
                guestRecordId: guestId
            });
            window.alertSuccessMessage(window.getI18n(ti18n, "REVOKE_SUCCESS"));
            this._applyOptimisticRevoke(guestId);
            // Reload guests after successful revoke
            this._loadGuests();
        } catch (error) {
            console.error('Guest revoke error:', error);
            window.alertErrorMessage(window.getI18n(ti18n, "REVOKE_ERROR"));
            throw error;
        }
    },

    _loadReviewerUsage: function() {
        if (!this._isInWorkspaceContext()) {
            return RSVP.Promise.resolve();
        }
        var workspaceId = LocalStorageHelper.getInstance().getCurrentWorkspaceId();
        if (!workspaceId) {
            return RSVP.Promise.resolve();
        }
        return TrebbleClientAPIHelper.getInstance().getWorkspaceUsage({
            workspaceId: workspaceId
        }).then((function(usageData) {
            if (usageData) {
                this._reviewerUsage = this._extractReviewerUsage(usageData);
            }
        }).bind(this)).catch(function(error) {
            console.error('Failed to load reviewer usage:', error);
            // Non-fatal: _computeCanInviteMoreGuests will allow optimistically
        });
    },

    _extractReviewerUsage: function(usageData) {
        // Try multiple response shapes the backend may return
        // (mirrors the resilient extraction logic in SettingsPage2Controller)
        var current = null;
        var limit = null;

        // Extract current reviewer count
        var currentSources = [
            usageData.reviewerUsage && usageData.reviewerUsage.current,
            usageData.reviewerUsage && usageData.reviewerUsage.currentCount,
            usageData.reviewersUsedThisMonth,
            usageData.reviewersThisBillingCycle,
            usageData.reviewerCount && usageData.reviewerCount.current
        ];
        for (var i = 0; i < currentSources.length; i++) {
            if (currentSources[i] !== null && currentSources[i] !== undefined) {
                current = currentSources[i];
                break;
            }
        }

        // Extract reviewer limit
        var limitSources = [
            usageData.reviewerUsage && usageData.reviewerUsage.limit,
            usageData.reviewerUsage && usageData.reviewerUsage.maxCount,
            usageData.reviewerLimit,
            usageData.maxReviewers,
            usageData.reviewerCount && usageData.reviewerCount.max,
            usageData.planLimits && usageData.planLimits.reviewers,
            usageData.effectiveLimits && usageData.effectiveLimits.reviewers
        ];
        for (var j = 0; j < limitSources.length; j++) {
            if (limitSources[j] !== null && limitSources[j] !== undefined) {
                limit = limitSources[j];
                break;
            }
        }

        return { current: current, limit: limit };
    },

    _loadGuests: function() {
        var guestsPromise = TrebbleClientAPIHelper.getInstance().getGuestsForProject({
            projectId: this._projectId
        });
        var usagePromise = this._loadReviewerUsage();

        RSVP.Promise.all([guestsPromise, usagePromise]).then((function(results) {
            var result = results[0];
            var guests = Array.isArray(result) ? result : (result && result.guests ? result.guests : []);
            this._setGuestState(guests, this._computeCanInviteMoreGuests(guests), null, true);
        }).bind(this)).catch((function(error) {
            console.error('Failed to load guests for project:', error);
            var canInviteMoreGuests = this._hasReliableGuestCache
                ? this._computeCanInviteMoreGuests(this._currentGuests)
                : false;
            this._setGuestState(
                this._currentGuests,
                canInviteMoreGuests,
                window.getI18n(ti18n, "LOAD_GUESTS_ERROR"),
                this._hasReliableGuestCache
            );
        }).bind(this));
    },

    buildModelAndRenderView: function() {
        const params = {
            projectId: this._projectId,
            projectName: this._projectName,
            currentGuests: [],
            canInviteMoreGuests: true,
            isLoadingGuests: true,
            guestLoadErrorMessage: null,
            onInvite: this.handleInvite.bind(this),
            onRevoke: this.handleRevoke.bind(this),
            i18nModule: ti18n
        };

        this.view = new ProjectSharePopupView(params);
        this.listenTo(this.view, "closed", this.onPopupClosed);

        this.view.render();

        // Load guests asynchronously after view is rendered
        this._loadGuests();

        return RSVP.Promise.resolve();
    }
});

export default ProjectSharePopupController;
